import { useNavigate, useRouterState, useSearch } from "@tanstack/react-router";
import { useMemo } from "react";
import { useShallow } from "zustand/react/shallow";
import { type RoleFilter, useDagStore } from "@/app/hooks/useDagStore";
import { omitDefaultSearchParams, SEARCH_DEFAULTS } from "@/app/routes/index.route";
import { calculateRoleCounts } from "@/app/utils/roleFilters";
import { DraggableTeamList } from "./DraggableTeamList";

// Shared wrapper component for consistent filter styling
function FilterWrapper({
  title,
  children,
  className = "",
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`space-y-2 ${className}`}>
      <h3 className="text-sm font-semibold text-stone-700 mb-2">{title}</h3>
      {children}
    </div>
  );
}

export function ScopeStatusFilter({ className = "" }: { className?: string }) {
  const scopeStatuses = ["Current", "Future"] as const;
  const router = useRouterState();
  const isDashboard = router.location.pathname === "/";

  // Always call hooks unconditionally
  const search = useSearch({ strict: false });
  const storeStatus = useDagStore(useShallow((state) => state.selectedScopeStatus));
  const storeToggle = useDagStore(useShallow((state) => state.toggleScopeStatus));
  const navigate = useNavigate({ from: "/" });

  const selectedScopeStatus = isDashboard
    ? (search?.scopeStatus ?? SEARCH_DEFAULTS.scopeStatus)
    : storeStatus;

  const onToggleScopeStatus = () => {
    if (isDashboard && search) {
      const newStatus: "Current" | "Future" =
        selectedScopeStatus === "Current" ? "Future" : "Current";
      void navigate({
        search: omitDefaultSearchParams({ ...search, scopeStatus: newStatus }),
      });
    } else {
      storeToggle();
    }
  };

  return (
    <FilterWrapper title="Scope Status" className={className}>
      <div className="space-y-2">
        {scopeStatuses.map((status) => (
          <label key={status} className="flex items-center">
            <input
              type="radio"
              name="scopeStatus"
              checked={selectedScopeStatus === status}
              onChange={() => onToggleScopeStatus()}
              className="h-4 w-4 text-blue-600 border-stone-300 focus:ring-blue-500"
              aria-label={`Filter by ${status} scope status`}
            />
            <span className="ml-2 text-sm text-stone-700">{status}</span>
          </label>
        ))}
      </div>
    </FilterWrapper>
  );
}

export function RoleFilter({ className = "" }: { className?: string }) {
  const roleFilters: RoleFilter[] = ["All", "Core Team", "Core Team - FT", "Contractors", "Other"];
  const router = useRouterState();
  const isDashboard = router.location.pathname === "/";

  // Always call hooks unconditionally
  const search = useSearch({ strict: false });
  const storeRoleFilter = useDagStore(useShallow((state) => state.selectedRoleFilter));
  const storeSetRoleFilter = useDagStore(useShallow((state) => state.setRoleFilter));
  const teamMembers = useDagStore(useShallow((state) => state.teamMembersFiltered));
  const navigate = useNavigate({ from: "/" });

  const selectedRoleFilter = isDashboard
    ? (search?.roleFilter ?? SEARCH_DEFAULTS.roleFilter)
    : storeRoleFilter;

  const roleCounts = useMemo(() => calculateRoleCounts(teamMembers), [teamMembers]);

  const setRoleFilter = (filter: RoleFilter) => {
    if (isDashboard && search) {
      void navigate({ search: omitDefaultSearchParams({ ...search, roleFilter: filter }) });
    } else {
      storeSetRoleFilter(filter);
    }
  };

  return (
    <FilterWrapper title="Role Filter" className={className}>
      <div className="space-y-2">
        {roleFilters.map((filter) => (
          <label key={filter} className="flex items-center">
            <input
              type="radio"
              name="roleFilter"
              checked={selectedRoleFilter === filter}
              onChange={() => setRoleFilter(filter)}
              className="h-4 w-4 text-blue-600 border-stone-300 focus:ring-blue-500"
              aria-label={`Filter by ${filter}`}
            />
            <span className="ml-2 text-sm text-stone-700">
              {filter} ({roleCounts[filter]})
            </span>
          </label>
        ))}
      </div>
    </FilterWrapper>
  );
}

export function TeamsFilter({ className = "" }: { className?: string }) {
  return <DraggableTeamList className={className} />;
}

export { ProjectFilter } from "./ProjectFilter";
